import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { ArrowRight, Users, Compass, MapPin } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import teamGroup from "@/assets/team/team-group.png";
import heroOffice from "@/assets/hero-office.webp";

export const Route = createFileRoute("/about/")({
  component: AboutIndex,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Despacho — RR Lex Juristas" },
      { name: "description", content: "Conoce a RR Lex Juristas: nuestro equipo, nuestra misión y nuestras oficinas en Sevilla, Portugal y Brasil." },
      { property: "og:title", content: "Despacho RR Lex Juristas" },
      { property: "og:description", content: "Equipo, misión y oficinas de RR Lex Juristas." },
      { property: "og:url", content: "https://rrlexjuristas.com/about" },
      { property: "og:image", content: teamGroup },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/about" }],
  }))(ctx)),
});

function AboutIndex() {
  const { tk } = useT();
  const heroBg = useMedia("home.office.hero", heroOffice, "Despacho RR Lex Juristas");

  const cards = [
    {
      to: "/about/equipo" as const,
      icon: Users,
      kicker: tk("about.index.team.kicker", "Equipo"),
      title: tk("about.index.team.title", "Conoce a nuestras abogadas"),
      desc: tk("about.index.team.desc", "Seis profesionales liderados por Charlene Ramos Rodrigues, una de las penalistas más reconocidas de España."),
    },
    {
      to: "/about/mision" as const,
      icon: Compass,
      kicker: tk("about.index.mision.kicker", "Misión"),
      title: tk("about.index.mision.title", "Nuestros valores y filosofía"),
      desc: tk("about.index.mision.desc", "Cercanía, transparencia y compromiso con la justicia social. Así trabajamos cada caso."),
    },
    {
      to: "/about/oficinas" as const,
      icon: MapPin,
      kicker: tk("about.index.oficinas.kicker", "Oficinas"),
      title: tk("about.index.oficinas.title", "Dónde estamos"),
      desc: tk("about.index.oficinas.desc", "Sedes en España, Portugal y Brasil. Atendemos también de forma remota."),
    },
  ];

  return (
    <Layout>
      <section className="relative border-b border-gold/15">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroBg.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("about.index.kicker", "Despacho")}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-4xl mx-auto">
            {tk("about.index.title.a", "Sobre el Despacho ")}
            <em className="not-italic gold-gradient">{tk("about.index.title.b", "RR Lex Juristas")}</em>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk("about.index.intro", "Descubre quiénes somos, en qué creemos y desde dónde defendemos tus derechos.")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col hover:border-gold/60 hover:bg-card/60 transition-colors"
              >
                <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-gold/80">
                  <Icon size={16} className="text-gold" />
                  <span>{c.kicker}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mt-5 group-hover:text-gold transition-colors">{c.title}</h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                  {tk("about.index.cta", "Ver más")} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
